<template>
    <v-container fluid class="py-0 shopTable">
        <v-data-table
            v-if="productListWithAppliedPricelist.length > 0"
            dense
            id="myTable"
            ref="datatable"
            :fixed-header="true"
            :headers="finalHeaders"
            :items="productListWithAppliedPricelist"
            :page.sync="page"
            :items-per-page.sync="itemsPerPage"
            :options.sync="options"
            :multi-sort="true"
            item-key="name"
            class="elevation-1"
            style="white"
            :loading="loadingData"
            :server-items-length="totalItemsServer"
            :footer-props="{
                'items-per-page-options': rowsPerPageItems,
            }"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            @update:page="handlePagination()"
            @update:items-per-page="handleItemsPerPage()"
            @update:sort-desc="handleSort()"
            @page-count="pageCount = $event"
            :hide-default-footer="true"
        >
            <template #item.img="{ item }">
                <ColumnImage :item="item" />
            </template>
            <template #item.pn="{ item }">
                <ColumnPartNumber :item="item" />
            </template>
            <template #item.name_en="{ item }">
                <ColumnDescription :item="item" />
            </template>
            <template #item.name_es="{ item }">
                <ColumnDescription :item="item" />
            </template>
            <template #item.sale_price="{ item }">
                <ColumnWebPrice :item="item" />
            </template>

            <template
                v-for="attr in validAttrColumnsHeaders"
                v-slot:[`item.${attr.value}`]="{ value }"
            >
                <v-tooltip top :key="attr.value">
                    <template v-slot:activator="{ on, attrs }">
                        <div
                            v-bind="attrs"
                            v-on="on"
                            class="d-flex justify-center align-center"
                            style="height: 100%"
                        >
                            <pre style="cursor: default">{{
                                value ? value.join() : '---'
                            }}</pre>
                        </div>
                    </template>
                    <span>{{ attr.text }}</span>
                </v-tooltip>
            </template>
            <template #item.action="{ item }">
                <ColumnAction :item="item" />
            </template>
        </v-data-table>
        <div v-else-if="productListWithAppliedPricelist.length === 0">
            <v-alert outlined text dense color="primary" class="mt-2">
                <TranslatableStringComponent :tstring="{string: 'shopPageFallback'}" />
            </v-alert>
        </div>
        <div v-else style="width:1495px;height:2000px" class="d-flex justify-center align-start">
            <v-progress-circular
            :size="50"
            color="primary"
            indeterminate
            ></v-progress-circular>
    </div>
    </v-container>
</template>

<script>
import { createHelpers } from 'vuex-map-fields'
import { mapGetters, mapState } from 'vuex'
import ColumnImage from '~/components/shop/shopColumnTemplate/ColumnImage.vue'
import ColumnPartNumber from '~/components/shop/shopColumnTemplate/ColumnPartNumber.vue'
import ColumnDescription from '~/components/shop/shopColumnTemplate/ColumnDescription.vue'
import ColumnWebPrice from '~/components/shop/shopColumnTemplate/ColumnWebPrice.vue'
import ColumnAction from '~/components/shop/shopColumnTemplate/ColumnAction.vue'
import ShopControlPanel from '~/components/shop/shopUtilities/ShopControlPanel.vue'
import TranslatableStringComponent from '~/components/TranslatableStringComponent.vue'
import _ from 'lodash'
const { mapFields } = createHelpers({
    getterType: 'shop/getField',
    mutationType: 'shop/updateField',
})

export default {
    name: 'ShopTable',
    components: {
        ColumnImage,
        ColumnPartNumber,
        ColumnDescription,
        ColumnWebPrice,
        ColumnAction,
        ShopControlPanel,
        TranslatableStringComponent,
    },
    data() {
        return {}
    },
    props: {
        validFacets: {
            type: Array,
            default: () => [],
        },
    },

    watch: {
        '$route.query': {
            handler: function () {
                this.page = 1
                this.sortBy = []
                this.sortDesc = []
                this.from = 0
                this.setInitialTableOptions()
                this.set_data()
            },
        },
        itemsPerPage(newVal) {
            this.options = {
                ...this.options,
                itemsPerPage: newVal,
            }
        },
        fetchProductList: {
            handler: function (newVal) {
                this.$emit('setProductList', newVal)
            },
            deep: true,
        },
    },
    computed: {
        ...mapFields([
            'filter',
            'loadingData',
            'size',
            'from',
            'totalItemsServer',
            'options',
            'page',
            'itemsPerPage',
            'pageCount',
            'rowsPerPageItems',
            'sortBy',
            'sortDesc',
        ]),
        ...mapGetters({
            productListWithAppliedPricelist:
                'shop/productListWithAppliedPricelist',
        }),
        validAttrColumnsHeaders() {
            return this.validFacets.map((i) => {
                return {
                    text: i.title[this.$i18n.locale],
                    value: `attributes.${i.title.en}`,
                    align: 'center',
                    sortable: true,
                    isAttr: true,
                }
            })
        },
        colHeaders() {
            if (this.productListWithAppliedPricelist.length > 0) {
                const attrs = Object.keys(
                    this.productListWithAppliedPricelist[0].attributes
                )
                return attrs.length > 0 ? attrs : []
            } else {
                return []
            }
        },
        finalHeaders() {
            return [
                {
                    text: '',
                    value: 'action',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: this.localize('image'),
                    align: 'center',
                    sortable: false,
                    value: 'img',
                },
                {
                    text: this.localize('partNumber'),
                    align: 'start',
                    sortable: true,
                    value: 'pn',
                    cellClass: 'noWrap',
                },
                { text: this.localize('description'), value: 'name_en', sortable: false },
                {
                    text: this.localize('webPrice'),
                    value: 'sale_price',
                    align: 'center',
                },
                {
                    text: this.localize('stdPkgQty'),
                    value: 'pkgqty',
                    align: 'center',
                    sortable: true,
                },
                ...this.validAttrColumnsHeaders,
            ]
        },
    },
    async mounted() {
        this.setInitialTableOptions()
        await this.$store.dispatch('shop/set_elastic_data')
        if(this.productListWithAppliedPricelist.length > 0)
            this.$nextTick(() => {
                this.DoubleScroll(
                    document.getElementsByClassName('v-data-table__wrapper')[0]
                )
            })
    },
    methods: {
        set_data() {
            this.$store.dispatch('shop/set_elastic_data')
        },
        handlePagination() {
            this.from = (this.page - 1) * this.size
            this.set_data()
        },
        handleItemsPerPage() {
            this.size = this.itemsPerPage
            this.from = 0
            this.set_data()
        },
        handleSort() {
            this.size = this.itemsPerPage
            this.from = 0
            this.set_data()
        },
        DoubleScroll(element) {
            var scrollbar = document.createElement('div')
            scrollbar.appendChild(document.createElement('div'))
            scrollbar.style.overflow = 'auto'
            scrollbar.style.overflowY = 'hidden'
            scrollbar.firstChild.style.width =
                document.getElementsByClassName('v-data-table__wrapper')[0]
                    .firstChild.scrollWidth + 'px'
            scrollbar.firstChild.style.height = '1px'
            scrollbar.firstChild.appendChild(document.createTextNode('\xA0'))
            scrollbar.onscroll = function () {
                element.scrollLeft = scrollbar.scrollLeft
            }
            element.onscroll = function () {
                scrollbar.scrollLeft = element.scrollLeft
            }
            document.getElementById('myTable').insertBefore(scrollbar, element)
        },
        setInitialTableOptions() {
            if (this.$route.query.Categories) {
                this.$store.commit('shop/SET_ITEMS_PER_PAGE', 20)
                this.$store.commit('shop/SET_SIZE', 20)
            } else {
                this.$store.commit('shop/SET_ITEMS_PER_PAGE', 8)
                this.$store.commit('shop/SET_SIZE', 8)
            }
        },
    },
}
</script>

<style scoped>
* >>> table > thead > tr > th:nth-child(1),
* >>> table > tbody > tr > td:nth-child(1) {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 0;
    padding: 0px 8px !important;
}

* >>> table > thead > tr > th:nth-child(2),
* >>> table > tbody > tr > td:nth-child(2) {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 148px;
    padding: 0px 16px !important;
}

* >>> table > thead > tr > th:nth-child(3),
* >>> table > tbody > tr > td:nth-child(3) {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 250px;
    padding: 0px 16px !important;
}

* >>> table > thead > tr > th:nth-child(1),
* >>> table > thead > tr > th:nth-child(2),
* >>> table > thead > tr > th:nth-child(3) {
    z-index: 4 !important;
}

* >>> table > thead > tr > th {
    background: #0063a6 !important;
    color: #fff !important;
}

* >>> table > tbody > tr:nth-of-type(odd) > td:nth-child(1),
* >>> table > tbody > tr:nth-of-type(odd) > td:nth-child(2),
* >>> table > tbody > tr:nth-of-type(odd) > td:nth-child(3) {
    background-color: #f4f4f4;
}
* >>> table > tbody > tr:nth-of-type(even) > td:nth-child(1),
* >>> table > tbody > tr:nth-of-type(even) > td:nth-child(2),
* >>> table > tbody > tr:nth-of-type(even) > td:nth-child(3) {
    background-color: #fcfcfc;
}

* >>> tbody tr:nth-of-type(odd) {
    background-color: rgb(234 234 234 / 50%);
}

* >>> tbody tr:nth-of-type(even) {
    background-color: rgb(245 245 245 / 30%);
}

* >>> tbody tr:nth-of-type(odd):hover {
    background-color: rgb(234 234 234 / 50%) !important;
}

* >>> tbody tr:nth-of-type(even):hover {
    background-color: rgb(245 245 245 / 30%) !important;
}

* >>> table > thead > tr > th > span {
    color: #fff !important;
}
.v-data-table-header__icon,
.v-data-table-header__sort-badge {
    color: #fff !important;
}

* >>> table > thead > tr > th {
    white-space: nowrap !important;
}
</style>
